import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Sidebar, type NavItem } from './Sidebar';
import { Header } from './Header';

export interface AppShellProps {
  appName: string;
  appSubtitle?: string;
  navItems: NavItem[];
  currentPage: string;
  onNavigate: (pageId: string) => void;
  children: React.ReactNode;
  userName?: string;
  userInitials?: string;
  userEmail?: string;
  userRole?: string;
  onLogout?: () => void;
  onSearch?: () => void;
  breadcrumbs?: { label: string; page?: string }[];
  headerActions?: React.ReactNode;
  logoIcon?: React.ComponentType<{ className?: string }>;
  logoGradient?: string;
  banner?: React.ReactNode;
  onUserClick?: () => void;
  /** Badge counts keyed by nav item id */
  navBadges?: Record<string, number>;
}

type Theme = 'light' | 'dark' | 'system';

function usePersistedTheme(): { isDark: boolean; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem('nuance-theme') as Theme) ?? 'system';
  });

  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  const toggle = () => {
    const next: Theme = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('nuance-theme', next);
  };

  return { isDark, toggle };
}

export function AppShell({
  appName,
  appSubtitle,
  navItems,
  currentPage,
  onNavigate,
  children,
  userName,
  userInitials,
  userEmail,
  userRole,
  onLogout,
  onSearch,
  breadcrumbs,
  headerActions,
  logoIcon,
  logoGradient,
  banner,
  onUserClick,
  navBadges,
}: AppShellProps) {
  const { isDark, toggle } = usePersistedTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className={clsx('min-h-screen', isDark ? 'dark bg-gray-950' : 'bg-gray-50')}>
      <Sidebar
        appName={appName}
        appSubtitle={appSubtitle}
        navItems={navItems}
        currentPage={currentPage}
        onNavigate={onNavigate}
        isDarkMode={isDark}
        onToggleTheme={toggle}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        userName={userName}
        userInitials={userInitials}
        userEmail={userEmail}
        userRole={userRole}
        onLogout={onLogout}
        logoIcon={logoIcon}
        logoGradient={logoGradient}
        onUserClick={onUserClick}
        navBadges={navBadges}
      />
      <div
        className={clsx(
          'transition-all duration-200',
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        {banner}
        <Header
          breadcrumbs={breadcrumbs}
          onSearch={onSearch}
          isDarkMode={isDark}
          actions={headerActions}
        />
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
